<template>
  <div class="inbox-page">
    <!-- ================= 顶部工具条 ================= -->
    <header class="inbox-header">
      <div class="inbox-header__main">
        <h1 class="inbox-title">收件箱</h1>
        <span class="inbox-count">共 {{ total }} 条</span>
      </div>
      <div class="inbox-header__actions">
        <el-button
          type="primary"
          size="small"
          icon="el-icon-refresh"
          :loading="loading"
          @click="loadList"
        >刷新</el-button>
        <el-button
          v-if="!readonlyMode"
          type="success"
          size="small"
          icon="el-icon-plus"
          @click="manualDialogVisible = true"
        >手动补录</el-button>
        <el-button
          v-if="!readonlyMode"
          type="danger"
          size="small"
          plain
          icon="el-icon-delete"
          :disabled="!total"
          @click="handleClearAll"
        >清空</el-button>
      </div>
    </header>

    <!-- 复制结果提示：移动端 el-message 在顶部容易被状态栏遮挡，这里单独做一条 -->
    <transition name="inbox-toast">
      <div v-if="toastText" class="inbox-toast">{{ toastText }}</div>
    </transition>

    <!-- ================= 列表主体 ================= -->
    <div v-loading="loading" class="inbox-body">
      <el-empty
        v-if="!loading && !list.length"
        description="还没有收件内容"
      >
        <div class="inbox-empty-tip">
          在手机上复制文本后，运行「保存到收件箱」快捷指令即可投递到这里
        </div>
      </el-empty>

      <ul v-else class="inbox-list">
        <li
          v-for="item in list"
          :key="item.id"
          class="inbox-card"
        >
          <!-- 卡片头：时间 + 操作 -->
          <div class="inbox-card__head">
            <span class="inbox-card__time">{{ formatTime(item.created_at) }}</span>
            <span class="inbox-card__spacer"></span>
            <el-button
              type="text"
              size="mini"
              :icon="copiedId === item.id ? 'el-icon-check' : 'el-icon-document-copy'"
              :class="['inbox-card__copy', { 'inbox-card__copy--done': copiedId === item.id }]"
              @click="handleCopy(item)"
            >{{ copiedId === item.id ? '已复制' : '复制' }}</el-button>
            <el-button
              v-if="!readonlyMode"
              type="text"
              size="mini"
              icon="el-icon-edit"
              class="inbox-card__copy"
              @click="openContentEditor(item)"
            >编辑</el-button>
            <el-button
              type="text"
              size="mini"
              icon="el-icon-link"
              class="inbox-card__copy"
              @click="handleCopyLinks(item)"
            >复制链接</el-button>
            <el-button
              v-if="!readonlyMode"
              type="text"
              size="mini"
              icon="el-icon-delete"
              class="inbox-card__delete"
              @click="handleDelete(item)"
            >删除</el-button>
          </div>

          <!-- 标题输入框：PC 端可见；聚焦后右侧浮现「保存」按钮 -->
          <div
            v-if="!isMobileViewport"
            class="inbox-card__title-row"
          >
            <!-- 已保存的标题以文字展示，点击进入编辑 -->
            <span
              v-if="item.title && editingTitleIds.indexOf(item.id) === -1"
              class="inbox-card__title-text"
              @click="startEditTitle(item)"
            >{{ item.title }}</span>

            <div
              v-else
              class="inbox-card__title-edit"
              :class="{ 'is-focused': focusedTitleId === item.id }"
            >
              <input
                :ref="`titleInput_${item.id}`"
                v-model="titleDrafts[item.id]"
                class="inbox-card__title-input"
                type="text"
                maxlength="255"
                placeholder="点此填写标题"
                @focus="focusedTitleId = item.id"
                @blur="handleTitleBlur(item)"
                @keyup.enter="handleSaveTitle(item)"
                @keyup.esc="cancelEditTitle(item)"
              >
              <!-- 聚焦后才出现保存按钮 -->
              <span
                v-if="focusedTitleId === item.id"
                class="inbox-card__title-save"
                @mousedown.prevent="handleSaveTitle(item)"
              >保存</span>
            </div>
          </div>

          <!-- 正文区：弹性占满卡片剩余高度，超出部分内部滚动（保证卡片等高） -->
          <div class="inbox-card__body">
            <div class="inbox-card__content">{{ item.content }}</div>
          </div>

          <!-- 内容里识别到的全部网址，逐条给出打开入口 -->
          <ul v-if="extractUrls(item.content).length" class="inbox-card__links">
            <li
              v-for="(url, urlIndex) in extractUrls(item.content)"
              :key="`${item.id}_url_${urlIndex}`"
            >
              <a :href="url" target="_blank" rel="noopener noreferrer">
                <i class="el-icon-link"></i>
                {{ url }}
              </a>
            </li>
          </ul>
        </li>
      </ul>

      <!-- 分页：条数多时才显示 -->
      <div v-if="total > pageSize" class="inbox-pager">
        <el-pagination
          small
          layout="prev, pager, next"
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- ================= 手动补录弹窗 ================= -->
    <el-dialog
      title="手动补录"
      :visible.sync="manualDialogVisible"
      width="min(560px, 92vw)"
      append-to-body
    >
      <el-input
        v-model="manualContent"
        type="textarea"
        :rows="6"
        maxlength="20000"
        show-word-limit
        placeholder="粘贴要留存的内容，或直接输入一段文字"
      />
      <div slot="footer">
        <el-button size="small" @click="manualDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="submitting"
          @click="handleManualSubmit"
        >保存</el-button>
      </div>
    </el-dialog>

    <!-- ================= 正文编辑弹窗 ================= -->
    <el-dialog
      title="编辑内容"
      :visible.sync="contentDialogVisible"
      width="min(680px, 92vw)"
      append-to-body
    >
      <div v-if="editingItem" class="content-editor">
        <div class="content-editor__meta">
          <span class="content-editor__meta-label">收件时间</span>
          <span>{{ formatTime(editingItem.created_at) }}</span>
        </div>
        <el-input
          v-model="contentDraft"
          type="textarea"
          :rows="14"
          maxlength="20000"
          show-word-limit
          placeholder="修改内容"
        />
      </div>
      <div slot="footer">
        <el-button size="small" @click="contentDialogVisible = false">取消</el-button>
        <el-button
          size="small"
          @click="restoreContentDraft"
        >还原</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="savingContent"
          @click="handleSaveContent"
        >保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchInboxList,
  createInboxItem,
  updateInboxTitle,
  updateInboxContent,
  deleteInboxItem,
  clearInbox,
} from '../../../api/modules/inbox'

/**
 * 复制成功/失败提示的显示时长（毫秒）。
 * 2.5s：成功反馈默认停留时长（动效性格清单要求）。
 */
const TOAST_DURATION = 2500

export default {
  name: 'AppInbox',

  data() {
    return {
      loading: false,
      submitting: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 50,
      /** 顶部轻提示文案，空串表示不显示 */
      toastText: '',
      /** 轻提示的定时器句柄 */
      toastTimer: null,
      /** 刚复制成功的记录 id，用于按钮变成对勾；2.5s 后自动清空 */
      copiedId: null,
      manualDialogVisible: false,
      manualContent: '',
      /** 正文编辑弹窗是否可见 */
      contentDialogVisible: false,
      /** 当前正在编辑正文的收件记录（弹窗元信息展示用） */
      editingItem: null,
      /** 正文编辑草稿 */
      contentDraft: '',
      /** 正文保存中标记 */
      savingContent: false,
      /** 各条收件的标题草稿，key 为收件 ID：{ [id]: '标题' } */
      titleDrafts: {},
      /** 当前正在编辑标题的收件 ID 列表（有已保存标题时点击才进入编辑态） */
      editingTitleIds: [],
      /** 当前获得焦点的标题输入框对应的收件 ID，null 表示无 */
      focusedTitleId: null,
      /** 视口是否为移动端（标题编辑仅 PC 端提供） */
      isMobileViewport: false,
    }
  },

  created() {
    this.syncViewport()
    // 标题编辑只在 PC 端提供，窗口尺寸变化时同步一次
    window.addEventListener('resize', this.syncViewport)
    this.loadList()
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.syncViewport)
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
    }
    if (this._copiedTimer) {
      clearTimeout(this._copiedTimer)
      this._copiedTimer = null
    }
  },

  computed: {
    /**
     * 只读模式：URL 带 freeView 参数（免登录只读分享）时，隐藏补录/删除/清空等写操作。
     * 该机制与项目里其他模块的免登录分享保持一致：
     * 未登录也能查看和复制，但不能改动数据。
     */
    readonlyMode() {
      return Boolean(
        this.$route.query &&
        Object.prototype.hasOwnProperty.call(this.$route.query, 'freeView')
      )
    },
  },

  methods: {
    /**
     * 同步移动端视口标记。
     * 标题编辑是 PC 端的便利功能（网格卡片里点了就改），
     * 手机端卡片窄、以快速复制为主，不展示该输入框。
     */
    syncViewport() {
      this.isMobileViewport = window.innerWidth <= 768
    },

    /**
     * 进入标题编辑态。
     * @param {Object} item 收件记录
     */
    startEditTitle(item) {
      if (this.titleDrafts[item.id] === undefined) {
        this.$set(this.titleDrafts, item.id, item.title || '')
      }
      if (this.editingTitleIds.indexOf(item.id) === -1) {
        this.editingTitleIds.push(item.id)
      }
      // 等输入框渲染出来再聚焦
      this.$nextTick(() => {
        const input = this.$refs[`titleInput_${item.id}`]
        const el = Array.isArray(input) ? input[0] : input
        if (el && el.focus) {
          el.focus()
        }
      })
    },

    /**
     * 标题输入框失焦。
     *
     * 只有「原本就没有标题」的条目才在失焦时自动保存（此时草稿为空即取消编辑）；
     * 已有标题的条目走「点保存按钮」提交，避免误触丢失原值。
     * @param {Object} item 收件记录
     */
    handleTitleBlur(item) {
      this.focusedTitleId = null

      if (item.title) {
        return
      }

      const draft = String(this.titleDrafts[item.id] || '').trim()

      if (!draft) {
        // 空草稿：退出编辑态，恢复成占位输入框
        this.$set(this.titleDrafts, item.id, '')
        return
      }

      this.handleSaveTitle(item)
    },

    /**
     * 保存标题。
     * @param {Object} item 收件记录
     */
    async handleSaveTitle(item) {
      const draft = String(this.titleDrafts[item.id] || '').trim()

      if (draft === String(item.title || '')) {
        // 没有变化，直接退出编辑态
        this.exitTitleEdit(item.id)
        return
      }

      try {
        const updated = await updateInboxTitle(item.id, draft)
        // 用后端返回值回填，保证与库内一致
        this.$set(item, 'title', (updated && updated.title) || '')
        this.$set(this.titleDrafts, item.id, (updated && updated.title) || '')
        this.exitTitleEdit(item.id)
        this.showToast(draft ? '标题已保存' : '标题已清空')
      } catch (error) {
        // 拦截器已提示，保留编辑态让用户重试
      }
    },

    /**
     * 取消标题编辑，丢弃草稿。
     * @param {Object} item 收件记录
     */
    cancelEditTitle(item) {
      this.$set(this.titleDrafts, item.id, item.title || '')
      this.exitTitleEdit(item.id)
    },

    /**
     * 退出标题编辑态。
     * @param {number} id 收件 ID
     */
    exitTitleEdit(id) {
      const index = this.editingTitleIds.indexOf(id)
      if (index !== -1) {
        this.editingTitleIds.splice(index, 1)
      }
      if (this.focusedTitleId === id) {
        this.focusedTitleId = null
      }
    },

    /**
     * 拉取收件列表。
     */
    async loadList() {
      this.loading = true
      try {
        const data = await fetchInboxList({
          page: this.page,
          pageSize: this.pageSize,
        })
        this.list = (data && data.list) || []
        this.total = (data && data.total) || 0

        // 为每条收件初始化标题草稿（v-model 要求属性存在才有响应性）
        // 正在编辑中的条目不覆盖，避免刷新打断用户输入
        this.list.forEach((item) => {
          if (this.editingTitleIds.indexOf(item.id) === -1) {
            this.$set(this.titleDrafts, item.id, item.title || '')
          }
        })

        // 列表刷新后清理已不存在的编辑态与标题草稿，避免 ID 集合无限增长
        const visibleIds = this.list.map(item => item.id)
        this.editingTitleIds = this.editingTitleIds.filter(id => visibleIds.includes(id))
        Object.keys(this.titleDrafts).forEach((draftId) => {
          if (!visibleIds.includes(Number(draftId))) {
            this.$delete(this.titleDrafts, draftId)
          }
        })
      } catch (error) {
        // request 拦截器已统一弹提示，这里只保证页面状态可用
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },

    /**
     * 切换分页。
     * @param {number} nextPage 目标页码
     */
    handlePageChange(nextPage) {
      this.page = nextPage
      this.loadList()
    },

    /**
     * 展示顶部轻提示。
     * @param {string} text 提示文案
     */
    showToast(text) {
      this.toastText = text
      if (this.toastTimer) {
        clearTimeout(this.toastTimer)
      }
      this.toastTimer = setTimeout(() => {
        this.toastText = ''
      }, TOAST_DURATION)
    },

    /**
     * 复制单条内容到剪贴板。
     *
     * iOS Safari 只在「用户手势直接触发」的调用链里允许 navigator.clipboard.writeText，
     * 且要求页面处于安全上下文（https）。因此优先走异步剪贴板 API，
     * 失败时回退到 textarea + execCommand('copy') 的老方案。
     * @param {Object} item 收件记录
     */
    async handleCopy(item) {
      const content = String(item.content || '')

      if (!content) {
        this.showToast('内容为空，无法复制')
        return
      }

      const copied = await this.copyToClipboard(content)
      this.showToast(copied ? '已复制到剪贴板' : '复制失败，请长按内容手动复制')

      // 反馈动效：复制成功后按钮图标变对勾，2.5s 后自动隐身恢复。
      // 与 toast 同步，动画不作为唯一反馈通道（图标 + 文案 + toast 三重反馈）。
      if (copied) {
        this.markCopied(item.id)
      }
    },

    /**
     * 把某条记录的复制按钮标记为「已完成」，2.5 秒后自动恢复。
     * 采用单例定时器：快速连点不同卡片时只保留最后一次的计时，
     * 避免大量定时器堆积和「旧的定时器把新的状态清掉」的竞态。
     * @param {string|number} id 记录 id
     */
    markCopied(id) {
      this.copiedId = id
      if (this._copiedTimer) {
        clearTimeout(this._copiedTimer)
      }
      this._copiedTimer = setTimeout(() => {
        this.copiedId = null
        this._copiedTimer = null
      }, 2500)
    },

    /**
     * 把文本写入系统剪贴板。
     * @param {string} content 待复制文本
     * @returns {Promise<boolean>} 是否复制成功
     */
    async copyToClipboard(content) {
      // 方案一：现代异步剪贴板 API（https 下可用）
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(content)
          return true
        } catch (error) {
          // 继续尝试兜底方案
        }
      }

      // 方案二：隐藏 textarea + execCommand，兼容不支持异步 API 的浏览器
      try {
        const textarea = document.createElement('textarea')
        textarea.value = content
        // 避免复制瞬间页面跳动到输入框位置
        textarea.style.position = 'fixed'
        textarea.style.top = '-9999px'
        textarea.style.left = '-9999px'
        textarea.setAttribute('readonly', 'readonly')
        document.body.appendChild(textarea)

        // iOS 需要先聚焦再选中，否则 selection 不生效
        textarea.focus()
        textarea.select()
        textarea.setSelectionRange(0, content.length)

        const succeeded = document.execCommand('copy')
        document.body.removeChild(textarea)
        return Boolean(succeeded)
      } catch (error) {
        return false
      }
    },

    /**
     * 从内容里提取全部网址，用于逐条给出「打开原链接」入口。
     * 一条收件里可能同时含多个链接（如「公告：xxx 下载：yyy」），全部返回。
     * @param {string} content 正文
     * @returns {string[]} 去重后的网址数组，没有则返回空数组
     */
    extractUrls(content) {
      const matched = String(content || '').match(/https?:\/\/[^\s，。；、）)"'<>【】]+/g)
      if (!matched) {
        return []
      }
      // 同一链接在正文里重复出现时只保留一条
      return Array.from(new Set(matched))
    },

    /**
     * 打开正文编辑弹窗。
     * @param {Object} item 收件记录
     */
    openContentEditor(item) {
      this.editingItem = item
      this.contentDraft = String(item.content || '')
      this.contentDialogVisible = true
    },

    /**
     * 把编辑草稿还原成打开弹窗时的原始内容。
     */
    restoreContentDraft() {
      if (this.editingItem) {
        this.contentDraft = String(this.editingItem.content || '')
        this.showToast('已还原为原始内容')
      }
    },

    /**
     * 保存编辑后的正文。
     */
    async handleSaveContent() {
      if (!this.editingItem) {
        return
      }

      const draft = String(this.contentDraft || '').trim()

      if (!draft) {
        this.showToast('内容不能为空')
        return
      }

      if (draft === String(this.editingItem.content || '')) {
        this.contentDialogVisible = false
        return
      }

      this.savingContent = true
      try {
        const updated = await updateInboxContent(this.editingItem.id, draft)
        // 用后端返回值回填列表项，保证与库内一致
        this.$set(this.editingItem, 'content', (updated && updated.content) || draft)
        this.contentDialogVisible = false
        this.showToast('内容已保存')
      } catch (error) {
        // 拦截器已提示，保留弹窗让用户重试
      } finally {
        this.savingContent = false
      }
    },

    /**
     * 复制该条内容里的全部链接（每行一个）。
     * @param {Object} item 收件记录
     */
    async handleCopyLinks(item) {
      const urls = this.extractUrls(item.content)

      if (!urls.length) {
        this.showToast('这条内容里没有链接')
        return
      }

      const copied = await this.copyToClipboard(urls.join('\n'))
      this.showToast(copied ? `已复制 ${urls.length} 个链接` : '复制失败，请长按链接手动复制')
    },

    /**
     * 删除单条收件。
     * @param {Object} item 收件记录
     */
    async handleDelete(item) {
      try {
        await this.$confirm('删除后不可恢复，确认删除这条收件内容？', '删除确认', {
          type: 'warning',
          confirmButtonText: '删除',
          cancelButtonText: '取消',
        })
      } catch (error) {
        // 用户取消
        return
      }

      try {
        await deleteInboxItem(item.id)
        this.showToast('已删除')
        // 删掉当前页最后一条时回退一页，避免停留在空页
        if (this.list.length === 1 && this.page > 1) {
          this.page -= 1
        }
        await this.loadList()
      } catch (error) {
        // 拦截器已提示
      }
    },

    /**
     * 清空全部收件。
     */
    async handleClearAll() {
      try {
        await this.$confirm(
          `将删除全部 ${this.total} 条收件内容，且不可恢复，确认清空？`,
          '清空确认',
          {
            type: 'warning',
            confirmButtonText: '清空',
            cancelButtonText: '取消',
          }
        )
      } catch (error) {
        // 用户取消
        return
      }

      try {
        await clearInbox()
        this.page = 1
        this.showToast('已清空')
        await this.loadList()
      } catch (error) {
        // 拦截器已提示
      }
    },

    /**
     * 提交手动补录内容。
     */
    async handleManualSubmit() {
      const content = String(this.manualContent || '').trim()

      if (!content) {
        this.showToast('内容不能为空')
        return
      }

      this.submitting = true
      try {
        await createInboxItem(content)
        this.manualDialogVisible = false
        this.manualContent = ''
        this.page = 1
        this.showToast('已保存')
        await this.loadList()
      } catch (error) {
        // 拦截器已提示
      } finally {
        this.submitting = false
      }
    },

    /**
     * 把后端返回的时间字符串格式化成「MM-DD HH:mm」。
     * @param {string} rawTime 后端 created_at
     * @returns {string}
     */
    formatTime(rawTime) {
      if (!rawTime) {
        return ''
      }

      const date = new Date(rawTime)
      if (Number.isNaN(date.getTime())) {
        return String(rawTime)
      }

      const pad = value => String(value).padStart(2, '0')
      const monthDay = `${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
      const hourMinute = `${pad(date.getHours())}:${pad(date.getMinutes())}`

      // 跨年时才补上年份，日常列表更清爽
      const isSameYear = date.getFullYear() === new Date().getFullYear()
      return isSameYear ? `${monthDay} ${hourMinute}` : `${date.getFullYear()}-${monthDay} ${hourMinute}`
    },
  },
}
</script>

<style scoped>
.inbox-page {
  /* 撑满内容区（已扣除 12px 内边距）；移动端独立模式下由 Layout 的 .standalone-page 提供高度 */
  height: 100%;
  min-height: 0;
  background-color: transparent;
  padding: 0;
  box-sizing: border-box;
  overflow: auto;
}

/* ================= 顶部工具条 ================= */
.inbox-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.inbox-header__main {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.inbox-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.inbox-count {
  font-size: 13px;
  color: #909399;
}

.inbox-header__actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
}

/* ================= 轻提示 ================= */
.inbox-toast {
  position: fixed;
  left: 50%;
  top: 18vh;
  transform: translateX(-50%);
  z-index: 3000;
  padding: 9px 20px;
  border-radius: 20px;
  background-color: rgba(0, 0, 0, 0.78);
  color: #fff;
  font-size: 14px;
  white-space: nowrap;
  pointer-events: none;
}

.inbox-toast-enter-active,
.inbox-toast-leave-active {
  transition: opacity 0.2s ease;
}

.inbox-toast-enter,
.inbox-toast-leave-to {
  opacity: 0;
}

/* ================= 列表 ================= */
.inbox-body {
  min-height: 200px;
}

.inbox-empty-tip {
  margin-top: 8px;
  font-size: 13px;
  color: #909399;
  line-height: 1.7;
}

.inbox-list {
  list-style: none;
  margin: 0;
  padding: 0;
  /* PC 端固定 3 列；手机上降为单列（见下方媒体查询） */
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  /* 所有卡片等高：行内拉伸 */
  align-items: stretch;
}

.inbox-card {
  /* 网格 item 撑满单元格，宽度由列宽决定而非内容长短 */
  width: 100%;
  box-sizing: border-box;
  background-color: var(--surface-1);
  border-radius: 8px;
  padding: 12px 14px;
  box-shadow: var(--shadow-surface-1);
  /* 卡片内部纵向排布，配合固定高度让正文区内部滚动 */
  display: flex;
  flex-direction: column;
  /* 固定高度保证同排卡片完全一致 */
  height: 260px;
  overflow: hidden;
}

.inbox-card__head {
  display: flex;
  align-items: center;
  /* 列宽较窄时允许按钮换行，避免三个操作挤成两行错位 */
  flex-wrap: wrap;
  gap: 6px 10px;
  margin-bottom: 8px;
  /* 头部不参与收缩，始终完整显示 */
  flex-shrink: 0;
}

.inbox-card__time {
  font-size: 12px;
  color: #909399;
  /* 时间不允许被压缩换行 */
  flex-shrink: 0;
}

.inbox-card__spacer {
  flex: 1;
  min-width: 0;
}

.inbox-card__copy {
  color: #409eff !important;}

/* 复制成功态：图标换成对勾、颜色转为成功绿，并带一次轻微回弹（overshoot）。
   2.5s 后由 JS 清空 copiedId 自动隐身，不占空间。 */
.inbox-card__copy--done {
  color: #52c41a !important;
  animation: inbox-copied-pop 0.32s cubic-bezier(0.34, 1.4, 0.64, 1);
}

@keyframes inbox-copied-pop {
  0%   { transform: scale(1); }
  45%  { transform: scale(1.14); }
  100% { transform: scale(1); }
}

@media (prefers-reduced-motion: reduce) {
  .inbox-card__copy--done {
    animation: none;
  }
}

.inbox-card__delete {
  color: #f56c6c !important;
}

/* ================= 标题行（PC 端） ================= */
.inbox-card__title-row {
  margin-bottom: 8px;
  /* 标题行不参与收缩，始终完整显示 */
  flex-shrink: 0;
}

/* 已保存的标题：单行省略，点击进入编辑 */
.inbox-card__title-text {
  display: inline-block;
  max-width: 100%;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  line-height: 1.6;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inbox-card__title-text:hover {
  color: #409eff;
}

/* 编辑态容器：输入框 + 聚焦时浮现的保存按钮 */
.inbox-card__title-edit {
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease;
}

.inbox-card__title-edit.is-focused {
  border-bottom-color: #dcdfe6;
}

.inbox-card__title-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 4px 0;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  font-family: inherit;
}

.inbox-card__title-input::placeholder {
  font-weight: 400;
  color: #c0c4cc;
}

.inbox-card__title-save {
  flex-shrink: 0;
  font-size: 12px;
  color: #409eff;
  cursor: pointer;
  user-select: none;
  padding: 2px 4px;
}

.inbox-card__title-save:hover {
  color: #66b1ff;
}

/* 正文区：占满卡片剩余高度，内容超出时内部滚动（卡片总高不变） */
.inbox-card__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  /* 滚动条细化，避免占掉太多横向空间 */
  scrollbar-width: thin;
}

.inbox-card__body::-webkit-scrollbar {
  width: 6px;
}

.inbox-card__body::-webkit-scrollbar-thumb {
  background-color: #dcdfe6;
  border-radius: 3px;
}

.inbox-card__body::-webkit-scrollbar-thumb:hover {
  background-color: #c0c4cc;
}

.inbox-card__content {
  font-size: 14px;
  line-height: 1.75;
  color: #303133;
  /* 保留快捷指令传来的换行，同时让超长单词/网址自动折行 */
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.inbox-card__links {
  list-style: none;
  margin: 8px 0 0;
  padding: 8px 0 0;
  border-top: 1px dashed #ebeef5;
  /* 链接区固定在卡片底部，不参与收缩 */
  flex-shrink: 0;
  /* 链接过多时最多显示 3 条，其余内部滚动 */
  max-height: 68px;
  overflow-y: auto;
}

.inbox-card__links li {
  font-size: 12px;
  line-height: 1.9;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inbox-card__links a {
  color: #409eff;
  text-decoration: none;
}

.inbox-card__links a:hover {
  text-decoration: underline;
}

/* ================= 分页 ================= */
.inbox-pager {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

/* ================= 正文编辑弹窗 ================= */
.content-editor__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 12px;
  color: #909399;
}

.content-editor__meta-label {
  color: #c0c4cc;
}

/* 编辑区域内的文本域加高，方便长文修改 */
.content-editor ::v-deep .el-textarea__inner {
  font-family: inherit;
  font-size: 14px;
  line-height: 1.75;
}

/* ================= 移动端适配 ================= */
@media (max-width: 768px) {
  .inbox-page {
    /* 独立模式全屏渲染：高度交给 Layout 的 .standalone-page（100% / 100dvh） */
    height: 100%;
    min-height: 0;
    padding: 12px;
    /* 避开 iPhone 底部 Home Indicator 区域 */
    padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  }

  /* 手机上强制单列，卡片撑满屏宽；取消固定高度，让内容自然撑开 */
  .inbox-list {
    grid-template-columns: 1fr;
  }

  .inbox-card {
    height: auto;
  }

  /* 手机端不滚动卡片内部，交给页面本身滚动 */
  .inbox-card__body {
    overflow: visible;
  }

  .inbox-card__links {
    max-height: none;
    overflow: visible;
  }

  .inbox-title {
    font-size: 18px;
  }

  /* 手机上操作按钮换行占满整行，避免被挤到屏幕外 */
  .inbox-header__actions {
    margin-left: 0;
    width: 100%;
  }

  .inbox-header__actions .el-button {
    flex: 1;
    margin-left: 0;
  }

  .inbox-card {
    padding: 11px 12px;
  }

  .inbox-card__content {
    font-size: 15px;
  }

  /* 触屏最小点击热区 */
  .inbox-card__head .el-button--mini {
    padding: 6px 4px;
    font-size: 13px;
  }
}
</style>
